import { useState, useRef, useCallback } from "react";
import { Upload, ArrowLeft, X } from "lucide-react";
import Cropper from "react-easy-crop";
import localization from "services/localization";
import { toast } from "react-toastify";

export default function ImageUpload({
  onResourceUpload,
  onBackClick,
  isModal = false,
  appUser,
  userData,
  loading,
  schoolId,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspectRatio] = useState(1);
  const [originalFile, setOriginalFile] = useState(null);

  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFile(e.dataTransfer.files[0]);
  };

  const handleChange = (e) => {
    if (!e.target.files?.length) return;
    handleFile(e.target.files[0]);
  };

  const handleFile = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file!");
      return;
    }
    setOriginalFile(file);
    const reader = new FileReader();
    reader.onload = () => setRawImage(reader.result);
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback(
    async (_, croppedAreaPixels) => {
      if (!rawImage) return;

      const canvas = document.createElement("canvas");
      const img = new Image();
      img.src = rawImage;
      await new Promise((res) => (img.onload = res));

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        img,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
      );

      canvas.toBlob((blob) => {
        if (blob) setCroppedImage(blob);
      }, originalFile?.type || "image/jpeg");
    },
    [rawImage, originalFile],
  );

  const handleUpload = async () => {
    if (!croppedImage || !originalFile || loading) return;

    try {
      const formData = new FormData();
      formData.append("files[]", croppedImage, originalFile.name);
      formData.append("file_size", originalFile.size);

      formData.append("to_rag", String(false));
      formData.append("visibility_scope", "public");
      formData.append("school_id", schoolId);
      formData.append("user_id", appUser?.id);
      formData.append("enabled", true);

      await onResourceUpload(formData);

      setRawImage(null);
      setCroppedImage(null);
      setOriginalFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Upload failed", error);
      toast.error(error?.response?.data?.error || "Failed to upload image!");
    }
  };

  return (
    <div className="resource-upload">
      {!isModal && (
        <button onClick={onBackClick} className="resource-upload-back-btn">
          <ArrowLeft size={20} />
          {localization.back_to_library || "Back to Library"}
        </button>
      )}

      <div
        className={`image-upload-container drag-zone ${
          dragActive ? "drag-active" : ""
        }`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        {!rawImage && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleChange}
              className="drag-input"
            />
            <div className="drag-content">
              <Upload size={48} />
              <p>{dragActive ? "Drop here" : "Drag and drop an image here"}</p>
              <p className="drag-or-text">or</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="drag-browse-btn"
              >
                {localization.browse_files || "Browse Files"}
              </button>
              <p className="drag-supported-formats">
                {localization.supported_formats ||
                  "Supported formats: JPG, JPEG, PNG"}
              </p>
            </div>
          </>
        )}

        {rawImage && (
          <div className="resource-upload-preview">
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "300px",
                background: "#f0f0f0",
              }}
            >
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <label className="d-flex align-items-center gap-2 mt-2">
              Zoom
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-grow-1"
              />
            </label>

            {croppedImage && (
              <ul className="preview-list mt-4">
                <li className="file-card">
                  <div className="file-card-info">
                    <span className="file-icon">🖼️</span>
                    <div className="file-text flex-1 min-w-0">
                      <p className="file-name">{originalFile.name}</p>
                      <p className="file-size">
                        {(originalFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setRawImage(null);
                      setCroppedImage(null);
                      setOriginalFile(null);
                    }}
                    className="remove-resource-btn"
                  >
                    <X size={16} />
                  </button>
                </li>
              </ul>
            )}

            <div className="resource-upload-actions mt-4 flex gap-2">
              <button
                onClick={() => {
                  setRawImage(null);
                  setCroppedImage(null);
                  setOriginalFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="resource-upload-cancel"
              >
                {localization.cancel || "Cancel"}
              </button>

              <button
                onClick={handleUpload}
                disabled={!croppedImage || loading}
                className="resource-upload-submit"
              >
                {localization.upload || "Upload"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
